/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Link } from '@fluentui/react/lib/Link';
import { Shimmer, ShimmerElementType } from '@fluentui/react/lib/Shimmer';
import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { LuisText } from 'src/core/components/LuisText';
import { Page } from 'src/core/components/Page';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { routeTable } from 'src/core/utils/routeTable';
import { QnaMakerEditKnowledgeBase } from 'src/qnaMaker/appShell/qnaMakerEditPage/QnaMakerEditKonwledgeBase';

const messages = defineMessages({
    pageTitle: {
        id: 'qnaMaker.qnaMakerEditPage.pageTitle',
        defaultMessage: 'Edit questions in { currentProjectName }'
    },
    unstructuredSources: {
        id: 'qnaMaker.qnaMakerEditPage.unstructuredSources',
        defaultMessage: '{unstructuredSources} unstructured sources.'
    },
    viewSources: {
        id: 'qnaMaker.qnaMakerEditPage.viewSources',
        defaultMessage: 'View sources'
    },
    questionAnswerPair: {
        id: 'qnaMaker.qnaMakerEditPage.questionAnswerPair',
        defaultMessage: 'Question answer pairs'
    }
});

/**
 * Renders the edit page for the QnA Maker service.
 */
export const QnaMakerEditPage = observer(() => {
    const { localize } = useLocalization();
    const { rootStore, dispatcher, uiStore } = useGlobalState();
    const { currentQnaMakerProjectStore } = rootStore;
    const history = useHistory();
    const {
        sourcesStoreHouse: { items, tracker }
    } = currentQnaMakerProjectStore;

    const unstructuredSources = items.reduce((acc, item) => (item.contentStructureKind === 'unstructured' ? acc + 1 : acc), 0);

    React.useEffect(() => {
        if (!uiStore.isSidebarCollapsed) {
            dispatcher.dispatch('setIsSidebarCollapsed', { isSidebarCollapsed: true });
        }
    }, []);

    return (
        <Page
            size="container-fluid"
            pageName="QnaMakerEditPage"
            title={localize(messages.pageTitle, { currentProjectName: currentQnaMakerProjectStore.name })}
            reactToZoomLevel={false}>
            <Stack verticalFill tokens={{ childrenGap: 10 }}>
                <Stack.Item>
                    <EditableTitle title={localize(messages.pageTitle, { currentProjectName: currentQnaMakerProjectStore.name })} />

                    {tracker.isRunning ? (
                        <Shimmer
                            shimmerElements={[{ type: ShimmerElementType.line, width: '70px', height: 15 }]}
                            styles={{ root: { marginTop: '15px', marginRight: '10px' } }}
                        />
                    ) : (
                        <LuisText>
                            {localize(messages.unstructuredSources, { unstructuredSources })} &nbsp;
                            <Link onClick={() => history.push(routeTable.qnaMaker.manageSourcesPage(currentQnaMakerProjectStore.name))}>
                                {localize(messages.viewSources)}
                            </Link>
                        </LuisText>
                    )}
                </Stack.Item>

                <Stack.Item styles={{ root: { flex: 1 } }}>
                    <QnaMakerEditKnowledgeBase />
                </Stack.Item>
            </Stack>
        </Page>
    );
});
